import { useState } from 'react';
import type { AlertChannelKind } from '@vigie/shared';
import { api } from '../api.js';
import { Card, Empty, Field, Notice } from '../components.js';
import { useAction, useAsync } from '../hooks.js';

const KINDS: AlertChannelKind[] = ['email', 'slack', 'discord', 'webpush'];

export function Channels() {
  const channels = useAsync(() => api.channels(), []);
  const sites = useAsync(() => api.sites(), []);
  const { run, busy, error } = useAction();

  const [kind, setKind] = useState<AlertChannelKind>('email');
  const [target, setTarget] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [p256dh, setP256dh] = useState('');
  const [auth, setAuth] = useState('');
  const [minScore, setMinScore] = useState(6);
  const [siteId, setSiteId] = useState('');
  const [tested, setTested] = useState<{ id: string; ok: boolean; error?: string } | null>(null);

  const create = async () => {
    const config =
      kind === 'email'
        ? { kind, to: target }
        : kind === 'webpush'
          ? { kind, endpoint, p256dh, auth }
          : { kind, webhookUrl: target };
    const created = await run(() =>
      api.createChannel({ kind, config: config as never, minScore, siteId: siteId || null }),
    );
    if (created) {
      setTarget('');
      setEndpoint('');
      setP256dh('');
      setAuth('');
      channels.reload();
    }
  };

  return (
    <>
      <header className="topbar">
        <h1>Canaux d’alerte</h1>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {tested ? (
          <Notice kind={tested.ok ? 'success' : 'error'}>
            {tested.ok ? 'Message de test envoyé.' : `Échec du test : ${tested.error}`}
          </Notice>
        ) : null}

        <Card title="Ajouter un canal">
          <div className="row">
            <div>
              <label>Type</label>
              <select value={kind} onChange={(event) => setKind(event.target.value as AlertChannelKind)}>
                {KINDS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Score minimum</label>
              <input
                type="number"
                min={0}
                max={10}
                value={minScore}
                onChange={(event) => setMinScore(Number(event.target.value))}
              />
            </div>
            <div>
              <label>Site</label>
              <select value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                <option value="">Tous les sites</option>
                {(sites.data ?? []).map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {kind === 'webpush' ? (
            <div className="row">
              <div>
                <label>Endpoint</label>
                <input value={endpoint} onChange={(event) => setEndpoint(event.target.value)} className="mono" />
              </div>
              <div>
                <label>Clé p256dh</label>
                <input value={p256dh} onChange={(event) => setP256dh(event.target.value)} className="mono" />
              </div>
              <div>
                <label>Secret auth</label>
                <input value={auth} onChange={(event) => setAuth(event.target.value)} className="mono" />
              </div>
            </div>
          ) : (
            <Field
              label={kind === 'email' ? 'Adresse email' : 'URL du webhook entrant'}
              help={
                kind === 'email'
                  ? 'Au-delà de 3 hits dans la même exécution, les alertes sont groupées en un digest.'
                  : 'URL https fournie par Slack ou Discord.'
              }
            >
              <input
                value={target}
                onChange={(event) => setTarget(event.target.value)}
                placeholder={kind === 'email' ? 'ops@dinno.fr' : 'https://hooks.slack.com/services/…'}
              />
            </Field>
          )}

          <button type="button" className="primary" onClick={create} disabled={busy}>
            Ajouter le canal
          </button>
        </Card>

        <Card title="Canaux configurés" tight>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Destination</th>
                <th className="num">Seuil</th>
                <th>Site</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(channels.data ?? []).map((channel) => (
                <tr key={channel.id}>
                  <td><span className="badge accent">{channel.kind}</span></td>
                  <td className="mono truncate" style={{ maxWidth: 320 }}>
                    {'to' in channel.config
                      ? channel.config.to
                      : 'webhookUrl' in channel.config
                        ? channel.config.webhookUrl
                        : 'endpoint' in channel.config
                          ? channel.config.endpoint
                          : '—'}
                  </td>
                  <td className="num">{channel.minScore}</td>
                  <td className="small muted">
                    {channel.siteId
                      ? (sites.data ?? []).find((site) => site.id === channel.siteId)?.name ?? channel.siteId
                      : 'tous'}
                  </td>
                  <td>
                    <span className={channel.enabled ? 'badge ok' : 'badge'}>
                      {channel.enabled ? 'actif' : 'désactivé'}
                    </span>
                  </td>
                  <td>
                    <div className="inline">
                      <button
                        type="button"
                        className="small"
                        disabled={busy}
                        onClick={async () => {
                          const result = await run(() => api.testChannel(channel.id));
                          if (result) setTested({ id: channel.id, ...result });
                        }}
                      >
                        Tester
                      </button>
                      <button
                        type="button"
                        className="small"
                        disabled={busy}
                        onClick={async () => {
                          await run(() => api.updateChannel(channel.id, { enabled: !channel.enabled }));
                          channels.reload();
                        }}
                      >
                        {channel.enabled ? 'Désactiver' : 'Activer'}
                      </button>
                      <button
                        type="button"
                        className="small danger"
                        disabled={busy}
                        onClick={async () => {
                          if (!confirm('Supprimer ce canal ?')) return;
                          await run(() => api.deleteChannel(channel.id));
                          channels.reload();
                        }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {channels.data && channels.data.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <Empty>Aucun canal : les hits détectés n’enverront aucune alerte.</Empty>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
